import type {
  DataFetchParams,
  DataFetchResponse,
} from '@/components/DataTable';
import { SortOrder } from '@kirin/api-client/contracts/helpers';
import { listSecurityGroups } from '@kirin/api-client/api/security-groups';
import type { SecurityGroup } from '@kirin/api-client/contracts/security-groups';
import { apiClient } from '@/lib/api-client';

/** Sortable fields on the Security Groups table. */
export const SECURITY_GROUP_SORT_FIELDS = {
  NAME: 'name',
  USER_COUNT: 'userCount',
  CREATED_BY: 'createdBy',
} as const;

/**
 * Display name for a group's creator, and the value the Created by column
 * sorts on — so the ordering always matches the text on screen.
 * Empty string for an unattributed group, which sorts last (see `compare`).
 */
export function creatorLabel(group: SecurityGroup): string {
  return group.createdBy?.name || group.createdBy?.email || '';
}

/**
 * Order two groups by the requested field.
 *
 * Blank creators sort last in BOTH directions: an absence is not a name, so
 * letting it float to the top on a descending sort reads as noise. Same rule
 * the Users page applies to its Security Group column.
 */
function compare(
  a: SecurityGroup,
  b: SecurityGroup,
  field: string,
  order: SortOrder
): number {
  const direction = order === SortOrder.Desc ? -1 : 1;

  if (field === SECURITY_GROUP_SORT_FIELDS.USER_COUNT) {
    return ((a.userCount ?? 0) - (b.userCount ?? 0)) * direction;
  }

  if (field === SECURITY_GROUP_SORT_FIELDS.CREATED_BY) {
    const aLabel = creatorLabel(a);
    const bLabel = creatorLabel(b);
    if (!aLabel && !bLabel) return 0;
    if (!aLabel) return 1;
    if (!bLabel) return -1;
    return aLabel.localeCompare(bLabel) * direction;
  }

  return a.name.localeCompare(b.name) * direction;
}

/**
 * Adapt the unpaginated security-groups endpoint to the DataTable's
 * server-shaped contract.
 *
 * The API deliberately returns every group in one response — the set is small
 * and admin-curated, and the Policies scope selector already consumes the whole
 * list. Rather than add pagination to the API purely to satisfy the table, this
 * sorts and slices locally. That is safe here precisely because the full set is
 * in hand: unlike the alerts table, sorting never sees only part of the data.
 * The search box matches name or description, case-insensitively.
 */
export async function fetchSecurityGroupsPage(
  organizationId: string,
  params: DataFetchParams
): Promise<DataFetchResponse<SecurityGroup>> {
  const groups = await listSecurityGroups(apiClient, organizationId);
  const term = params.search?.trim().toLowerCase();
  const matching = term
    ? groups.filter(
        group =>
          group.name.toLowerCase().includes(term) ||
          !!group.description?.toLowerCase().includes(term)
      )
    : groups;

  const field = params.sort?.field ?? SECURITY_GROUP_SORT_FIELDS.NAME;
  const order = params.sort?.order ?? SortOrder.Asc;
  const sorted = [...matching].sort((a, b) => compare(a, b, field, order));

  const { pageIndex, pageSize } = params.pagination;
  const start = pageIndex * pageSize;
  const page = sorted.slice(start, start + pageSize);

  return {
    data: page,
    pagination: {
      page: pageIndex + 1,
      limit: pageSize,
      total: sorted.length,
      hasMore: start + page.length < sorted.length,
    },
    // No column filters on this table, so nothing is ever active.
    meta: { activeFilters: [] },
  };
}
